import type { createFormatter } from "use-intl/core";
import type { AppLocale } from "./config";

type Formatter = ReturnType<typeof createFormatter>;
type DateTimeArguments = Parameters<Formatter["dateTime"]>;

/**
 * What joins a date and a time in each locale: CLDR's medium date-time pattern without the "at"
 * wording some ICU versions use. Fixed here because server and browser ICU versions disagree on
 * it, and a different separator would fail hydration.
 */
const DATE_TIME_SEPARATORS: Record<AppLocale, string> = {
  en: ", ",
  ar: "، ",
  de: ", ",
  es: ", ",
  fr: ", ",
  hi: ", ",
  id: ", ",
  it: ", ",
  ja: " ",
  ko: " ",
  nl: ", ",
  pl: ", ",
  "pt-BR": ", ",
  ru: ", ",
  tr: " ",
  "zh-CN": " ",
};

/** Keep the date/time separator identical across server and browser ICU versions. */
export const stableFormatter = (formatter: Formatter, locale: AppLocale): Formatter => ({
  ...formatter,
  dateTime: (
    value: DateTimeArguments[0],
    format?: DateTimeArguments[1] | DateTimeArguments[2],
    overrides?: DateTimeArguments[2],
  ) =>
    format === "dateTime"
      ? `${formatter.dateTime(value, "date", overrides)}${DATE_TIME_SEPARATORS[locale]}${formatter.dateTime(value, { hour: "numeric", minute: "2-digit", ...overrides })}`
      : typeof format === "object"
        ? formatter.dateTime(value, format)
        : formatter.dateTime(value, format, overrides),
});
